<template>
  <div
    id="allFeaturedRatesBox"
    class="featured-box container-fluid topMargin"
    v-if="itemsRendered"
  >
    <div class="featured-head row">
      <div class="col col-xs-12 title">
        <h4>Today's Featured Rates</h4>
        <h5 v-if="currentTab.averageRate">
          {{currentTab.productId == 34 ? "vs the FDIC 1-yr CD Nat'l Avg:" : "vs. the FDIC Nat'l Avg:" }}
          <transition name="component-fade" mode="out-in">
            <span v-bind:key="currentTab.key"
              >{{ $filters.percentageFilter(currentTab.averageRate) }} APY </span
            >
          </transition>
          <da-tooltip class="apy-tooltip-span" :class="apyTooltipIconCSSClass" :iconVersion="iconVersion" :title="apyTitleText"></da-tooltip>
        </h5>
      </div>
      <font-awesome-icon
        class="trophy-icon"
        size="lg"
        :icon="['fas', 'trophy-alt']"
      ></font-awesome-icon>
    </div>
    <div class="featured-head row">
      <div class="col col-xs-12 font-style mb-10 title">
        <how-da-get-paid></how-da-get-paid><br />
        <ad-disclosure-tooltip></ad-disclosure-tooltip>
      </div>
    </div>
    <div class="featured-rates-body row">
      <div v-if="tabs.length > 1" class="nav-tabs">
        <div
          class="col-sm tab-name"
          v-for="(tab, tabIndex) in tabs"
          v-bind:key="tab.key"
          v-bind:class="['tab-button', { active: currentTab.name === tab.name }]"
          v-on:click.prevent="tabClick(tab, tabIndex)"
        >
          {{ tab.name }}
        </div>
        <hr
          class="active-tab-hr"
          ref="activeTabHr"
          v-bind:style="activeTabHrCss"
        />
      </div>

      <transition-group
        name="all-featured-rates-list"
        tag="table"
        class="table all-featured-rates-list"
        v-bind:style="listCss"
      >
        <tbody ref="listBody" v-bind:key="currentTab.key">
          <item
            v-for="ad in currentTab.ads"
            v-bind:key="ad.id"
            v-bind:ad="ad"
          ></item>
        </tbody>
      </transition-group>

      <div class="footer">
        <h4 class="all-rates-link">
          <transition name="component-fade" mode="out-in">
            <a v-bind:key="currentTab.key" v-bind:href="currentTab.url"
              >Compare All Rates ></a
            >
          </transition>
        </h4>
        <h6 class="featured-disclaimer">
          Sponsors listed are Member FDIC or NCUA insured.
        </h6>
        <h6 class="featured-disclaimer" v-if="isBonusAmountExists">
          * Certain restrictions and conditions may apply. See bank website for
          more details.
        </h6>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue, { defineComponent } from "vue";
import apiClient from "../../services/apiClient";
import Enumerable from "linq";
import "../../utils/filters";

import listItem from "./listItem.vue";

import analytics from "../../services/analytics";
import howDaGetPaid from "../../components/how-da-get-paid.vue";
import adDisclosureTooltip from "../../components/ad-disclosure-tooltip.vue";
import { faUnderline } from "@fortawesome/pro-light-svg-icons";
import DaTooltip from "../../components/da-tooltip.vue"


type tabItem = {
  key: string;
  name: string;
  productId: Number;
  clicked: boolean;
  url: string;
  averageRate: Number;
  ads: AdPlacement[];
};

type pagesName = {
  pageName: string;
  tabName: string;
};

let tabsData: Array<tabItem> = [
  {
    key: "da-fa2-sav",
    name: "Savings",
    productId: 9,
    clicked: false,
    url: "/savings",
    averageRate: 0,
    ads: []
  },
  {
    key: "da-fa2-cd",
    name: "CDs",
    productId: 34,
    clicked: false,
    url: "/cd",
    averageRate: 0,
    ads: []
  },
  {
    key: "da-fa2-chk",
    name: "Checking",
    productId: 13,
    clicked: false,
    url: "/checking",
    averageRate: 0,
    ads: []
  }
];

let pagesName: Array<pagesName> = [
  { pageName: "/checking/", tabName: "Checking" },
  { pageName: "/checking/[A-Za-z0-9\\-_]{1,999}\\.html", tabName: "Checking" },

  { pageName: "/cd/", tabName: "CDs" },
  { pageName: "/cd/[A-Za-z0-9\\-_]{1,999}\\.html", tabName: "CDs" },
  { pageName: "/cd/[A-Za-z0-9\\-_]{1,999}\\.aspx", tabName: "CDs" },
  { pageName: "/tools/ewp-calculator.aspx", tabName: "CDs" },
  { pageName: "/tools/break-cd-calculator.aspx", tabName: "CDs" },
];

export default defineComponent({
  components: {
    item: listItem,
    howDaGetPaid,
    adDisclosureTooltip,
    daTooltip: DaTooltip
  },
  props: {
    apyTooltipIconCSSClass: String,
  },
  data: function() {
    return {
      currentTab: {} as tabItem,
      tabs: tabsData,
      itemsRendered: false,
      listCss: { height: "" },
      activeTabHrCss: { width: "", 'margin-left': "" },
      pages: pagesName,
      apyTitleText: "",
      iconVersion: "3",
    };
  },
  watch: {
    currentTab() {
      this.$nextTick(() => {
        this.listCss.height = `${(this.$refs.listBody as any).clientHeight}px`;
        $(".tmpContent").css("min-height", `${(this.$refs.listBody as any).clientHeight + 320}px`);
      });
    }
  },
  mounted: function() {
    this.mountWidget();
  },
  methods: {
    async mountWidget() {
      let adZoneKeys = this.tabs.map(tab => tab.key);

      let adZones: AdZone[] = await apiClient.getAdZones(adZoneKeys);
      adZones = adZones.filter(x => x.placements.length > 0);
      if (!(adZones && adZones.length)) {
        return;
      }

      this.tabs = Enumerable.from(this.tabs)
        .join(
          adZones,
          "outer => outer.key" as any,
          "inner => inner.keyName" as any,
          "(o,i)=>{tab: o, ads: i.placements}" as any
        )
        .select((joinedObj: any) => mapAdWithTab(joinedObj))
        .toArray();

      this.activeTabHrCss.width = 100/this.tabs.length + "%";

      const productIds = this.tabs.map(tab => tab.productId);
      // If average-rates API won't return the result or throws an error it needs to handle and continues execution.
      const allAverageRates = await getAvgRateOfAllProducts(productIds).catch(
        reject => {
          return [];
        }
      );
      for (const avgRateObj of allAverageRates) {
        if (!avgRateObj.averageRates || avgRateObj.averageRates.length <= 0) {
          continue;
        }

        const avgRate = avgRateObj.averageRates[0].fdicNationalAverage;
        let tab = this.tabs.filter(tab => tab.productId === avgRateObj.id);
        tab[0].averageRate = avgRate;
        this.apyTitleText = "National average calculated and reported by the FDIC as of " + new Date(avgRateObj.averageRates[0].fdicDate).toLocaleDateString();;
      }
      this.itemsRendered = true;

      let page = this.pages.find(elem => window.location.pathname.match(elem.pageName) != null);
      let tabIndex = 0;
      if (page != null && page != undefined && page.tabName === "Checking")
        tabIndex = this.tabs.findIndex(t => t.name === "Checking");
      else if (page != null && page != undefined && page.tabName === "CDs")
        tabIndex = this.tabs.findIndex(t => t.name === "CDs");

      tabIndex = tabIndex < 0 ? 0 : tabIndex;
      this.tabClick(this.tabs[tabIndex], tabIndex);

      (<any>window).affixSidebar();
    },
    tabClick(tab: tabItem, tabIndex: number) {
      this.currentTab = tab;
      if (!this.currentTab.clicked) {
        this.currentTab.clicked = true;
        analytics.trackAdImpressions(this.currentTab.key, this.currentTab.ads);
      }

      this.activeTabHrCss['margin-left'] = (100/this.tabs.length * tabIndex).toFixed(2) + "%";

    }
  },
  computed:{
    isBonusAmountExists():boolean {
     return this.currentTab.ads.some( x=> x.account.bonusAmount >0 );
    }
  }
});
async function getAvgRateOfAllProducts(productIds: Number[]) {
  const averageRatePromises = productIds.map(prodId =>
    apiClient.getAverageRateOfToday(prodId)
  );
  const combinedAverageRatePromise = Promise.all(averageRatePromises);
  const averageRateOfProducts = await combinedAverageRatePromise;
  return averageRateOfProducts;
}

function mapAdWithTab(item: any) {
  const tabData = item.tab;

  return {
    key: tabData.key,
    name: tabData.name,
    productId: tabData.productId,
    url: tabData.url,
    clicked: false,
    averageRate: 0,
    ads: item.ads
  };
}
</script>
<style lang="scss" scoped>
#allFeaturedRatesBox {
  font-family: "Titillium Web";
  background-color: #fff;
  a {
    text-decoration: none;
  }
  .featured-head {
    display: flex;
    align-items: center;
    background-color: #1f548b;
    font-weight: bold;
    color: #fff;
    font-size: 16px;
    padding: 2px;
    .title {
      padding-left: 10px;
    }
    h4 {
      color: #fff;
      margin-top: 10px;
      margin-bottom: 5px;
    }
    h5 {
      font-size: 13px;
      color: #fff;
      font-weight: 500;
      margin-top: 5px;
    }
    .trophy-icon {
      color: #4699bb;
      margin-right: 10px;
    }
  }
  .featured-rates-body {
    border: 1px solid #d9d9d9;
    table.featured-rates {
      font-size: 14px;
      margin-bottom: 10px;
      tbody > tr:nth-child(even) {
        background-color: #f5f5f5;
      }
    }
    .all-rates-link {
      a {
        color: #ff9900;
        &:hover {
          text-decoration: underline;
        }
      }
      text-align: center;
      margin-bottom: 15px;
    }
    .featured-disclaimer {
      color: #999;
      font-weight: normal;
      font-style: italic;
      text-align: center;
      font-size: 12px;
    }
  }
}
.nav-tabs {
  position: relative;
  display: flex;
  .tab-name {
    flex-grow: 1;
    flex-basis: 0;
  }

  .tab-button {
    padding: 6px 10px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    color: #999;
    transition: all 0.15s ease-in-out;
    border-bottom:5px solid transparent;
    &:hover {
      border-bottom: 5px solid #ccc;
    }
    &.active {
      color: #4699bb;
    }
  }
}
.active-tab-hr {
  position: absolute;
  bottom: 0;
  margin: 0;
  border-bottom: 5px solid #4699bb;
  transition: all 0.3s ease-in-out;
}
.footer {
  width:100%;
  margin-bottom: 8px;
}

.component-fade {
  &-enter,
  &-leave-to {
    opacity: 0;
  }

  &-leave,
  &-enter-to {
    opacity: 1;
  }

  &-enter-active,
  &-leave-active {
    transition: opacity 0.25s ease-in-out;
  }
}

.all-featured-rates-list {
  transition: height 0.25s;

  &-enter,
  &-leave-to {
    opacity: 0;
  }

  &-leave,
  &-enter-to {
    opacity: 1;
  }

  &-enter-active,
  &-leave-active {
    position: absolute;
    transition: opacity 0.1s ease-in-out;
    width: 298px;
  }

  &-enter-active {
    transition-delay: 0.25s;
  }
}
.font-style{
  font-weight: 400;
  font-size: 13px;
}
.mb-10{
  margin-bottom: 10px;
}
</style>
